import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiRefreshCw, FiCheck, FiX, FiPlay, FiUser, FiShield, FiZap, FiAlertTriangle, FiCpu, FiFileText, FiTrendingUp, FiActivity } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import { getFeatureFlagsForUser, getEffectivePlanId, getPlanDisplayInfo, FeatureFlags } from '@/services/featureFlagsService';
import SafeIcon from '@/components/SafeIcon';

interface TestUser {
  id: string;
  user_id: string;
  email: string;
  name: string;
  role: string;
  subscription_plan_id: string | null;
  subscription_status: string;
  is_in_trial: boolean;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  analyses_used_this_month: number;
  is_active: boolean;
}

interface ExpectedBehavior {
  canRunAnalyses: boolean;
  analysesLimit: string;
  hasCounterfactual: boolean;
  hasInstitutionalReview: boolean;
  hasAuditPdf: boolean;
  hasComplianceApi: boolean;
  description: string;
}

const EXPECTED_BEHAVIORS: Record<string, ExpectedBehavior> = {
  'snaptest+none@snap-chart.ai': {
    canRunAnalyses: false,
    analysesLimit: '0',
    hasCounterfactual: false,
    hasInstitutionalReview: false,
    hasAuditPdf: false,
    hasComplianceApi: false,
    description: 'Cannot run AI analyses, sees upgrade dialog, no journal advanced features.'
  },
  'snaptest+starter@snap-chart.ai': {
    canRunAnalyses: true,
    analysesLimit: '10/month',
    hasCounterfactual: false,
    hasInstitutionalReview: false,
    hasAuditPdf: false,
    hasComplianceApi: false,
    description: 'Can run up to 10 analyses/month, gets blocked on 11th with upgrade prompt, no institutional features.'
  },
  'snaptest+pro@snap-chart.ai': {
    canRunAnalyses: true,
    analysesLimit: 'Unlimited',
    hasCounterfactual: true,
    hasInstitutionalReview: false,
    hasAuditPdf: false,
    hasComplianceApi: false,
    description: 'Unlimited analyses, Pro-level features (counterfactual, TDI base), but no audit/compliance.'
  },
  'snaptest+inst@snap-chart.ai': {
    canRunAnalyses: true,
    analysesLimit: 'Unlimited',
    hasCounterfactual: true,
    hasInstitutionalReview: true,
    hasAuditPdf: true,
    hasComplianceApi: false,
    description: 'Full institutional governance + audit features (Review Block, Audit PDF, TDI 850, Drift, Overtrading).'
  },
  'snaptest+enterprise@snap-chart.ai': {
    canRunAnalyses: true,
    analysesLimit: 'Unlimited',
    hasCounterfactual: true,
    hasInstitutionalReview: true,
    hasAuditPdf: true,
    hasComplianceApi: true,
    description: 'Everything unlocked including compliance, audit API, multi-user dashboards, white-label.'
  },
  'snaptest+admin@snap-chart.ai': {
    canRunAnalyses: true,
    analysesLimit: 'Unlimited (Admin)',
    hasCounterfactual: true,
    hasInstitutionalReview: true,
    hasAuditPdf: true,
    hasComplianceApi: true,
    description: 'Full access regardless of plan, unlimited analyses, no upgrade prompts.'
  },
  'snaptest+trial@snap-chart.ai': {
    canRunAnalyses: true,
    analysesLimit: 'Unlimited (Trial)',
    hasCounterfactual: true,
    hasInstitutionalReview: false,
    hasAuditPdf: false,
    hasComplianceApi: false,
    description: 'Active trial with Pro Trader access. Unlimited analyses during trial period.'
  }
};

const PlanQAPlayground: React.FC = () => {
  const navigate = useNavigate();
  const [testUsers, setTestUsers] = useState<TestUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<TestUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadTestUsers();
  }, []);

  const loadTestUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('get_qa_test_users');
      
      if (rpcError) throw rpcError;
      
      if (data?.success && data.users) {
        setTestUsers(data.users);
        if (data.users.length > 0 && !selectedUser) {
          setSelectedUser(data.users[0]);
        }
      } else if (data?.error) {
        setError(data.error);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load test users');
    } finally {
      setLoading(false);
    }
  };

  const ensureTestUsers = async () => {
    setActionLoading('ensure');
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('ensure_qa_test_users');
      
      if (rpcError) throw rpcError;
      
      if (data?.success) {
        setSuccessMessage('Test users created/updated successfully!');
        await loadTestUsers();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data?.error || 'Failed to create test users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create test users');
    } finally {
      setActionLoading(null);
    }
  };

  const updateTestUser = async (email: string, updates: { analyses_used?: number; is_in_trial?: boolean; trial_days?: number }) => {
    setActionLoading(email);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('update_qa_test_user', {
        p_email: email,
        p_analyses_used: updates.analyses_used ?? null,
        p_is_in_trial: updates.is_in_trial ?? null,
        p_trial_days_remaining: updates.trial_days ?? null
      });
      
      if (rpcError) throw rpcError;
      
      if (data?.success) {
        setSuccessMessage(`Updated ${email}`);
        await loadTestUsers();
        setTimeout(() => setSuccessMessage(null), 2000);
      } else {
        setError(data?.error || 'Failed to update user');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const getFeatureFlagsForTestUser = (user: TestUser): FeatureFlags => {
    return getFeatureFlagsForUser({
      subscription_plan_id: user.subscription_plan_id,
      analyses_used_this_month: user.analyses_used_this_month,
      role: user.role,
      is_in_trial: user.is_in_trial,
      trial_ends_at: user.trial_ends_at
    });
  };

  const getEffectivePlanForTestUser = (user: TestUser): string => {
    return getEffectivePlanId({
      subscription_plan_id: user.subscription_plan_id,
      role: user.role,
      is_in_trial: user.is_in_trial,
      trial_ends_at: user.trial_ends_at
    });
  };

  const getPlanBadgeColor = (planId: string | null, role: string, isInTrial: boolean): string => {
    if (role === 'admin') return 'from-amber-500 to-orange-500';
    if (isInTrial) return 'from-green-500 to-emerald-500';
    if (!planId) return 'from-gray-500 to-gray-600';
    switch (planId) {
      case 'starter': return 'from-blue-500 to-blue-600';
      case 'pro_trader': return 'from-purple-500 to-purple-600';
      case 'institutional_ai': return 'from-cyan-500 to-cyan-600';
      case 'enterprise': return 'from-amber-500 to-amber-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const renderFeatureCheck = (label: string, actual: boolean, expected: boolean) => {
    const matches = actual === expected;
    return (
      <div className={`flex items-center justify-between p-2 rounded-lg ${matches ? 'bg-green-500/10' : 'bg-red-500/20'}`}>
        <span className="text-sm text-gray-300">{label}</span>
        <div className="flex items-center gap-2">
          <span className={`text-xs ${actual ? 'text-green-400' : 'text-red-400'}`}>
            {actual ? 'Yes' : 'No'}
          </span>
          {matches ? (
            <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-400" />
          ) : (
            <SafeIcon icon={FiX} className="w-4 h-4 text-red-400" />
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-purple-400">
          <SafeIcon icon={FiRefreshCw} className="w-6 h-6 animate-spin" />
          <span>Loading QA Playground...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Plan QA Playground
            </h1>
            <p className="text-gray-400 mt-1">Test subscription plans and feature flags</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 rounded-xl bg-gray-700/50 text-gray-300 hover:bg-gray-700 transition-colors"
            >
              ← Back to Admin
            </button>
            <button
              onClick={ensureTestUsers}
              disabled={actionLoading === 'ensure'}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium hover:from-purple-500 hover:to-cyan-500 transition-all disabled:opacity-50"
            >
              {actionLoading === 'ensure' ? (
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
              ) : (
                <SafeIcon icon={FiPlay} className="w-4 h-4" />
              )}
              Initialize Test Users
            </button>
            <button
              onClick={loadTestUsers}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-700/50 text-gray-300 hover:bg-gray-700 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400"
          >
            {error}
          </motion.div>
        )}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-xl text-green-400"
          >
            {successMessage}
          </motion.div>
        )}

        {testUsers.length === 0 ? (
          <div className="text-center py-16 bg-gray-800/30 rounded-2xl border border-gray-700/50">
            <SafeIcon icon={FiUser} className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No Test Users Found</h3>
            <p className="text-gray-500 mb-6">Click "Initialize Test Users" to create QA test accounts</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Test Users List */}
            <div className="lg:col-span-1 space-y-4">
              <h2 className="text-lg font-semibold text-white mb-4">Test Users</h2>
              {testUsers.map((user) => {
                const effectivePlan = getEffectivePlanForTestUser(user);
                const planInfo = getPlanDisplayInfo(effectivePlan);
                const isSelected = selectedUser?.email === user.email;
                
                return (
                  <motion.button
                    key={user.email}
                    onClick={() => setSelectedUser(user)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isSelected
                        ? 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-purple-500/50'
                        : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-white">{user.name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${getPlanBadgeColor(user.subscription_plan_id, user.role, user.is_in_trial)} text-white`}>
                        {planInfo.name}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className="text-gray-500">Usage: {user.analyses_used_this_month}</span>
                      {user.is_in_trial && (
                        <span className="text-green-400">Trial Active</span>
                      )}
                      {user.role === 'admin' && (
                        <span className="text-amber-400">Admin</span>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Selected User Details */}
            {selectedUser && (
              <div className="lg:col-span-2 space-y-6">
                {/* User Info Card */}
                <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-white">{selectedUser.name}</h2>
                    <span className={`px-3 py-1 rounded-full bg-gradient-to-r ${getPlanBadgeColor(selectedUser.subscription_plan_id, selectedUser.role, selectedUser.is_in_trial)} text-white text-sm font-medium`}>
                      {getPlanDisplayInfo(getEffectivePlanForTestUser(selectedUser)).name}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-700/30 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">Plan ID</p>
                      <p className="text-white font-mono text-sm">{selectedUser.subscription_plan_id || 'none'}</p>
                    </div>
                    <div className="bg-gray-700/30 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">Role</p>
                      <p className="text-white font-mono text-sm">{selectedUser.role}</p>
                    </div>
                    <div className="bg-gray-700/30 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">Usage This Month</p>
                      <p className="text-white font-mono text-sm">{selectedUser.analyses_used_this_month}</p>
                    </div>
                    <div className="bg-gray-700/30 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">Trial Status</p>
                      <p className={`font-mono text-sm ${selectedUser.is_in_trial ? 'text-green-400' : 'text-gray-500'}`}>
                        {selectedUser.is_in_trial ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="border-t border-gray-700/50 pt-4">
                    <h3 className="text-sm font-medium text-gray-400 mb-3">Quick Actions</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => updateTestUser(selectedUser.email, { analyses_used: 0 })}
                        disabled={actionLoading === selectedUser.email}
                        className="px-3 py-1.5 text-xs rounded-lg bg-gray-700/50 text-gray-300 hover:bg-gray-700 transition-colors disabled:opacity-50"
                      >
                        Set Usage = 0
                      </button>
                      <button
                        onClick={() => updateTestUser(selectedUser.email, { analyses_used: 9 })}
                        disabled={actionLoading === selectedUser.email}
                        className="px-3 py-1.5 text-xs rounded-lg bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30 transition-colors disabled:opacity-50"
                      >
                        Set Usage = 9
                      </button>
                      <button
                        onClick={() => updateTestUser(selectedUser.email, { analyses_used: 10 })}
                        disabled={actionLoading === selectedUser.email}
                        className="px-3 py-1.5 text-xs rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors disabled:opacity-50"
                      >
                        Set Usage = 10 (Limit)
                      </button>
                      <button
                        onClick={() => updateTestUser(selectedUser.email, { is_in_trial: !selectedUser.is_in_trial, trial_days: 30 })}
                        disabled={actionLoading === selectedUser.email}
                        className={`px-3 py-1.5 text-xs rounded-lg transition-colors disabled:opacity-50 ${
                          selectedUser.is_in_trial
                            ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                            : 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                        }`}
                      >
                        {selectedUser.is_in_trial ? 'End Trial' : 'Start Trial'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Feature Flags Comparison */}
                <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiShield} className="w-5 h-5 text-purple-400" />
                    Feature Flags Verification
                  </h3>

                  {(() => {
                    const flags = getFeatureFlagsForTestUser(selectedUser);
                    const expected = EXPECTED_BEHAVIORS[selectedUser.email];

                    return (
                      <div className="space-y-4">
                        {/* Expected Behavior Description */}
                        {expected && (
                          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                            <p className="text-sm text-blue-300 flex items-start gap-2">
                              <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <span><strong>Expected:</strong> {expected.description}</span>
                            </p>
                          </div>
                        )}

                        {/* Feature Checks Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {renderFeatureCheck(
                            'Can Use AI Analysis',
                            flags.canUseAiAnalysis,
                            expected?.canRunAnalyses ?? true
                          )}
                          {renderFeatureCheck(
                            'Has Counterfactual Tracking',
                            flags.hasCounterfactualTracking,
                            expected?.hasCounterfactual ?? false
                          )}
                          {renderFeatureCheck(
                            'Has Institutional Review Block',
                            flags.hasInstitutionalReviewBlock,
                            expected?.hasInstitutionalReview ?? false
                          )}
                          {renderFeatureCheck(
                            'Has Audit PDF Export',
                            flags.hasAuditPdf,
                            expected?.hasAuditPdf ?? false
                          )}
                          {renderFeatureCheck(
                            'Has TDI Score',
                            flags.hasTdiScore,
                            flags.hasTdiScore // Always pass since TDI varies by plan
                          )}
                          {renderFeatureCheck(
                            'Has Behavioral Drift Detection',
                            flags.hasBehavioralDriftDetection,
                            expected?.hasInstitutionalReview ?? false
                          )}
                          {renderFeatureCheck(
                            'Has Overtrading Clusters',
                            flags.hasOvertradingClusters,
                            expected?.hasInstitutionalReview ?? false
                          )}
                          {renderFeatureCheck(
                            'Has Compliance & API',
                            flags.hasComplianceApi,
                            expected?.hasComplianceApi ?? false
                          )}
                        </div>

                        {/* Limits Section */}
                        <div className="mt-4 p-4 bg-gray-700/30 rounded-xl">
                          <h4 className="text-sm font-medium text-gray-300 mb-3">Analysis Limits</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-400">Limit</p>
                              <p className="text-white font-mono">
                                {flags.aiAnalysesLimit === null ? 'Unlimited' : `${flags.aiAnalysesLimit}/month`}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">Current Usage</p>
                              <p className="text-white font-mono">{selectedUser.analyses_used_this_month}</p>
                            </div>
                          </div>
                        </div>

                        {/* Full Flags JSON */}
                        <details className="mt-4">
                          <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-300 transition-colors">
                            View Full Feature Flags Object
                          </summary>
                          <pre className="mt-3 p-4 bg-gray-900/50 rounded-xl text-xs text-gray-300 overflow-auto max-h-64">
                            {JSON.stringify(flags, null, 2)}
                          </pre>
                        </details>
                      </div>
                    );
                  })()}
                </div>

                {/* Scenario Checklist */}
                <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiFileText} className="w-5 h-5 text-cyan-400" />
                    Test Scenarios
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="p-3 bg-gray-700/30 rounded-xl">
                      <p className="text-gray-300 mb-1 font-medium">1. Dashboard Access</p>
                      <p className="text-gray-500">Verify user can access /dashboard and see appropriate features</p>
                    </div>
                    <div className="p-3 bg-gray-700/30 rounded-xl">
                      <p className="text-gray-300 mb-1 font-medium">2. AI Analysis</p>
                      <p className="text-gray-500">Try to run an analysis and verify limit enforcement</p>
                    </div>
                    <div className="p-3 bg-gray-700/30 rounded-xl">
                      <p className="text-gray-300 mb-1 font-medium">3. Journal Features</p>
                      <p className="text-gray-500">Check TDI Score, Counterfactual, Audit PDF visibility</p>
                    </div>
                    <div className="p-3 bg-gray-700/30 rounded-xl">
                      <p className="text-gray-300 mb-1 font-medium">4. Upgrade Prompts</p>
                      <p className="text-gray-500">Verify upgrade dialogs appear for locked features</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanQAPlayground;
